// Blog server functions.
//   • Public reads (list + single post) return published posts only.
//   • The editor console is open to two roles: `admin` (full control, approves
//     and publishes) and `writer` (drafts, previews and submits for approval —
//     never publishes, never sees anything else in the admin console).
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  BLOG_COLUMNS,
  draftPromptSchema,
  upsertPostSchema,
  type BlogPost,
} from "./blog-shared";
import { z } from "zod";

/** Newest published posts for /blog. */
export const listBlogPosts = createServerFn({ method: "GET" }).handler(async (): Promise<BlogPost[]> => {
  try {
    const { readPublishedPosts } = await import("./blog.server");
    return await readPublishedPosts();
  } catch (e) {
    console.error("listBlogPosts failed", e);
    return [];
  }
});

/** One published post by slug for /blog/$slug. */
export const getBlogPost = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1).max(90) }).parse(input))
  .handler(async ({ data }): Promise<BlogPost | null> => {
    try {
      const { readPublishedPost } = await import("./blog.server");
      return await readPublishedPost(data.slug);
    } catch (e) {
      console.error("getBlogPost failed", e);
      return null;
    }
  });

type Ctx = { supabase: { rpc: (fn: string, args: unknown) => Promise<{ data: unknown }> }; userId: string };

async function hasRole(context: unknown, role: "admin" | "writer"): Promise<boolean> {
  const c = context as Ctx;
  const { data } = await c.supabase.rpc("current_user_has_role", { _role: role });
  return data === true;
}

export interface BlogRole {
  admin: boolean;
  writer: boolean;
}

/** Which blog console permissions does the signed-in member have? */
export const myBlogRole = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BlogRole> => {
    const [admin, writer] = await Promise.all([hasRole(context, "admin"), hasRole(context, "writer")]);
    return { admin, writer };
  });

async function assertEditor(context: unknown): Promise<BlogRole> {
  const [admin, writer] = await Promise.all([hasRole(context, "admin"), hasRole(context, "writer")]);
  if (!admin && !writer) throw new Error("Forbidden — the blog console needs an admin or writer role.");
  return { admin, writer };
}

async function assertAdmin(context: unknown) {
  if (!(await hasRole(context, "admin"))) throw new Error("Forbidden — only an admin can approve or publish.");
}

/**
 * Posts visible in the console. RLS narrows a writer to their own rows, so the
 * same query serves both roles.
 */
export const editorListBlogPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BlogPost[]> => {
    await assertEditor(context);
    const { data, error } = await context.supabase
      .from("blog_posts")
      .select(BLOG_COLUMNS)
      .order("updated_at", { ascending: false })
      .limit(300);
    if (error) throw error;
    return (data ?? []) as unknown as BlogPost[];
  });

/**
 * Create or update a post.
 *
 * Admins may publish outright. Writers can only save a draft or submit for
 * approval — the database trigger strips `published` regardless of payload, so
 * this is defence in depth rather than the only guard.
 */
export const saveBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => upsertPostSchema.parse(input))
  .handler(async ({ data, context }): Promise<BlogPost> => {
    const role = await assertEditor(context);
    const wantsPublish = role.admin && data.published;
    const status = role.admin
      ? wantsPublish
        ? "published"
        : data.status === "pending"
          ? "pending"
          : "draft"
      : data.status === "pending"
        ? "pending"
        : "draft";

    const base = {
      slug: data.slug.toLowerCase(),
      title: data.title,
      excerpt: data.excerpt,
      body: data.body,
      cover_image: data.cover_image,
      cover_alt: data.cover_alt,
      tag: data.tag,
      author: data.author,
      published: wantsPublish,
      status,
    };

    if (data.id) {
      const { data: existing } = await context.supabase
        .from("blog_posts")
        .select("published_at")
        .eq("id", data.id)
        .maybeSingle();
      const publishedAt = wantsPublish
        ? ((existing as { published_at: string | null } | null)?.published_at ?? new Date().toISOString())
        : null;
      const { data: row, error } = await context.supabase
        .from("blog_posts")
        .update({ ...base, ...(role.admin ? { published_at: publishedAt } : {}) })
        .eq("id", data.id)
        .select(BLOG_COLUMNS)
        .single();
      if (error) throw error;
      if (wantsPublish) await pingPublished((row as unknown as BlogPost).slug);
      return row as unknown as BlogPost;
    }

    const { data: row, error } = await context.supabase
      .from("blog_posts")
      .insert({
        ...base,
        author_id: context.userId,
        published_at: wantsPublish ? new Date().toISOString() : null,
      })
      .select(BLOG_COLUMNS)
      .single();
    if (error) throw error;
    if (wantsPublish) await pingPublished((row as unknown as BlogPost).slug);
    return row as unknown as BlogPost;
  });

/** Writer hands a finished draft to the admin for approval. */
export const submitForReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }): Promise<BlogPost> => {
    await assertEditor(context);
    const { data: row, error } = await context.supabase
      .from("blog_posts")
      .update({ status: "pending", submitted_at: new Date().toISOString() })
      .eq("id", data.id)
      .select(BLOG_COLUMNS)
      .single();
    if (error) throw error;
    return row as unknown as BlogPost;
  });

/** Admin approval — the only route to a live article. */
export const approveBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }): Promise<BlogPost> => {
    await assertAdmin(context);
    const now = new Date().toISOString();
    const { data: existing } = await context.supabase
      .from("blog_posts")
      .select("published_at")
      .eq("id", data.id)
      .maybeSingle();
    const { data: row, error } = await context.supabase
      .from("blog_posts")
      .update({
        published: true,
        status: "published",
        published_at: (existing as { published_at: string | null } | null)?.published_at ?? now,
        reviewed_at: now,
        reviewed_by: context.userId,
        review_note: null,
      })
      .eq("id", data.id)
      .select(BLOG_COLUMNS)
      .single();
    if (error) throw error;
    await pingPublished((row as unknown as BlogPost).slug);
    return row as unknown as BlogPost;
  });

/** Admin sends a submission back with a note, or unpublishes a live article. */
export const rejectBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ id: z.string().uuid(), note: z.string().trim().max(600).default("") }).parse(input),
  )
  .handler(async ({ data, context }): Promise<BlogPost> => {
    await assertAdmin(context);
    const { data: row, error } = await context.supabase
      .from("blog_posts")
      .update({
        published: false,
        published_at: null,
        status: "rejected",
        reviewed_at: new Date().toISOString(),
        reviewed_by: context.userId,
        review_note: data.note || "Changes requested.",
      })
      .eq("id", data.id)
      .select(BLOG_COLUMNS)
      .single();
    if (error) throw error;
    return row as unknown as BlogPost;
  });

/** Permanently remove a post. Writers may only bin their own unpublished drafts. */
export const deleteBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertEditor(context);
    const { error } = await context.supabase.from("blog_posts").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

/**
 * Push a freshly published article to news and search crawlers: IndexNow
 * (Bing/Yandex and friends) plus a Search Console re-submission of the regular
 * and Google News sitemaps, which is how Google News discovers new articles now
 * that sitemap pings are retired.
 */
async function pingPublished(slug: string) {
  try {
    const { submitToIndexNow, refreshSitemapsAuto } = await import("./indexnow.server");
    await Promise.all([
      submitToIndexNow([
        `https://palstars.com/blog/${slug}`,
        "https://palstars.com/blog",
        "https://palstars.com/",
      ]),
      refreshSitemapsAuto(),
    ]);
  } catch (e) {
    console.error("blog publish ping failed", e);
  }
}

/**
 * AI first draft. Uses the Lovable AI gateway with a Pal Stars house-style brief
 * and a compact snapshot of the site's own guides so the draft links internally
 * instead of inventing sources. Admins and assigned writers may use it.
 */
export const draftBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => draftPromptSchema.parse(input))
  .handler(async ({ data, context }): Promise<{ title: string; excerpt: string; body: string }> => {
    await assertEditor(context);
    const key = process.env["LOVABLE_API_KEY"];
    if (!key) throw new Error("AI drafting is not configured.");

    const { GUIDES } = await import("./guides-data");
    const related = GUIDES.filter((g) => {
      const q = data.topic.toLowerCase();
      return g.title.toLowerCase().includes(q.split(" ")[0] ?? "") || g.keywords.toLowerCase().includes(q);
    }).slice(0, 12);
    const guideList = (related.length ? related : GUIDES.slice(0, 12))
      .map((g) => `${g.title} → /guides/${g.slug}`)
      .join("\n");

    const system = [
      "You are the staff writer for Pal Stars (palstars.com), an unofficial Palworld fan wiki and Palworld OFFICIAL CARD GAME database.",
      "Write factual, specific, SEO and AEO optimised articles in British-neutral English. No hype, no emoji, no invented statistics.",
      "Never claim affiliation with Pocketpair or Bushiroad. Card numbers always use the printed English codes with the leading E (EBP01-001, ETD01-012TSP, ESOUL-002).",
      "Output plain Markdown only, using ## and ### headings, - lists, > pull quotes, [text](/internal-path) links, and ![alt](https://image-url) images.",
      "Never output a level-1 # heading (the page renders its own H1). Open with a single 40-60 word direct answer paragraph, then the sections.",
      "Link to at least three of these existing Pal Stars pages where relevant:",
      guideList,
      "Also useful: /prices (price chart), /marketplace (sealed product), /cards (card database), /collection (collection tracker), /deck-builder, /pack-calculator.",
    ].join("\n");

    const user = [
      `Topic: ${data.topic}`,
      data.angle ? `Angle / must cover: ${data.angle}` : "",
      data.tag ? `Category: ${data.tag}` : "",
      "Return JSON only, shaped: {\"title\": string (max 70 chars, keyword-led), \"excerpt\": string (max 155 chars), \"body\": string (Markdown, 700-1100 words, ends with a short FAQ section of 3 questions as ### headings)}",
    ]
      .filter(Boolean)
      .join("\n");

    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: { Authorization: `Bearer ${key}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "google/gemini-3.6-flash",
        messages: [
          { role: "system", content: system },
          { role: "user", content: user },
        ],
        response_format: { type: "json_object" },
      }),
    });
    if (!res.ok) {
      const detail = await res.text();
      console.error("draftBlogPost gateway error", res.status, detail.slice(0, 400));
      throw new Error(res.status === 429 ? "AI rate limit reached — try again shortly." : "AI drafting failed.");
    }
    const json = (await res.json()) as { choices?: { message?: { content?: string } }[] };
    const raw = json.choices?.[0]?.message?.content ?? "{}";
    let parsed: { title?: string; excerpt?: string; body?: string };
    try {
      parsed = JSON.parse(raw) as typeof parsed;
    } catch {
      parsed = { body: raw };
    }
    return {
      title: (parsed.title ?? data.topic).slice(0, 90),
      excerpt: (parsed.excerpt ?? "").slice(0, 300),
      body: parsed.body ?? "",
    };
  });
